import React, { createContext, useState, useContext, useEffect } from 'react';
import { authAPI } from '../services/api';

const AuthContext = createContext(null);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshTimer, setRefreshTimer] = useState(null);

  useEffect(() => {
    const token = localStorage.getItem('token');
    if (token) {
      loadUser();
      scheduleTokenRefresh();
    } else {
      setLoading(false);
    }
    return () => {
      if (refreshTimer) clearTimeout(refreshTimer);
    };
  }, []);

  const scheduleTokenRefresh = () => {
    if (refreshTimer) clearTimeout(refreshTimer);
    const token = localStorage.getItem('token');
    if (!token) return;

    try {
      const payload = JSON.parse(atob(token.split('.')[1]));
      const expiresIn = payload.exp * 1000 - Date.now();
      const refreshIn = Math.max(0, expiresIn - 5 * 60 * 1000);

      if (refreshIn > 0) {
        const timer = setTimeout(() => {
          refreshToken();
        }, refreshIn);
        setRefreshTimer(timer);
      }
    } catch (e) {
      console.error('Failed to parse token:', e);
    }
  };

  const refreshToken = async () => {
    try {
      const data = await authAPI.refreshToken();
      localStorage.setItem('token', data.access_token);
      scheduleTokenRefresh();
    } catch (error) {
      console.error('Failed to refresh token:', error);
    }
  };

  const loadUser = async () => {
    try {
      const userData = await authAPI.getCurrentUser();
      setUser(userData);
    } catch (error) {
      console.error('Failed to load user:', error);
      localStorage.removeItem('token');
    } finally {
      setLoading(false);
    }
  };

  const login = async (username, password) => {
    try {
      const data = await authAPI.login(username, password);
      localStorage.setItem('token', data.access_token);
      const userData = await authAPI.getCurrentUser();
      setUser(userData);
      scheduleTokenRefresh();
      return { success: true, user: userData };
    } catch (error) {
      localStorage.removeItem('token');
      return {
        success: false,
        error: error.response?.data?.detail || 'Login failed',
      };
    }
  };

  const logout = () => {
    if (refreshTimer) clearTimeout(refreshTimer);
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    localStorage.removeItem('webphone_creds');
    setUser(null);
    setRefreshTimer(null);
  };

  const value = {
    user,
    login,
    logout,
    loading,
    isAuthenticated: !!user,
    isAdmin: user?.is_admin || false,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
